/**
 * FTEXT Health Badge (Feb 2026)
 *
 * Δείχνει live την κατάσταση του τοπικού fiscal bridge:
 *   🟢 OK — όλα καλά
 *   🟡 Degraded — αργεί ή μερικά errors
 *   🔴 Critical — agent offline ή stuck jobs
 *
 * Polling κάθε 15s. Click → modal με λεπτομέρειες + κουμπί
 * «Επιδιόρθωση Τώρα» που καλεί /api/ftext/health/repair.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, PrinterCheck, RefreshCw, Wrench, BellRing } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const STATUS_MAP = {
  ok:       { color: "emerald", Icon: CheckCircle2,  label: "OK",
              btn: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
              icon: "text-emerald-600" },
  degraded: { color: "amber",   Icon: AlertTriangle, label: "Καθυστ.",
              btn: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
              icon: "text-amber-600" },
  critical: { color: "rose",    Icon: AlertTriangle, label: "ΣΦΑΛΜΑ",
              btn: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100",
              icon: "text-rose-600" },
  loading:  { color: "zinc",    Icon: Loader2,       label: "…",
              btn: "bg-zinc-50 text-zinc-500 border-zinc-200",
              icon: "text-zinc-500" },
};

const METRIC_ACCENTS = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber:   "border-amber-200 bg-amber-50 text-amber-800",
  rose:    "border-rose-200 bg-rose-50 text-rose-800",
  zinc:    "border-zinc-200 bg-zinc-50 text-zinc-800",
};
const METRIC_ACCENT_LABEL = {
  emerald: "text-emerald-500",
  amber:   "text-amber-500",
  rose:    "text-rose-500",
  zinc:    "text-zinc-500",
};

function fmtAge(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function FTextHealthBadge() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);
  // Auto-alert (2026-02): όταν το status γυρίσει σε ``critical`` και
  // ο χρήστης δεν το έχει snoozed, ανοίγουμε αυτόματα το dialog + ήχο.
  // Το ``snoozeUntil`` κρατιέται στο localStorage ώστε να μη φωνάζει
  // ξανά αμέσως μετά από page refresh.
  const [snoozeUntil, setSnoozeUntil] = useState(() => {
    const v = localStorage.getItem("ftext_alert_snooze_until");
    return v ? parseInt(v, 10) : 0;
  });
  const [alertOpen, setAlertOpen] = useState(false);
  const prevStatusRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await http.get("/ftext/health");
      setHealth(r.data);
    } catch (e) {
      // Silent — network glitch, keep old data.
      // console.debug("ftext health poll failed:", e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Auto-alert trigger: κάθε φορά που το ``status`` γίνει ``critical`` και
  // δεν είμαστε σε snooze period, ανοίγουμε popup + beep.
  useEffect(() => {
    if (!health) return;
    const now = Date.now();
    const cur = health.status;
    const prev = prevStatusRef.current;
    if (cur === "critical" && prev !== "critical" && now >= snoozeUntil) {
      setAlertOpen(true);
      // Discreet audio ping — 2 short beeps at 880Hz.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          [0, 250].forEach((delayMs) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = 880;
            gain.gain.value = 0.15;
            osc.connect(gain).connect(ctx.destination);
            const start = ctx.currentTime + delayMs / 1000;
            osc.start(start);
            osc.stop(start + 0.15);
          });
        }
      } catch { /* ignore audio errors */ }
    }
    prevStatusRef.current = cur;
  }, [health, snoozeUntil]);

  const snoozeAlert = (minutes) => {
    const until = Date.now() + minutes * 60 * 1000;
    setSnoozeUntil(until);
    localStorage.setItem("ftext_alert_snooze_until", String(until));
    setAlertOpen(false);
  };

  const runRepair = async () => {
    setRepairing(true);
    try {
      const r = await http.post("/ftext/health/repair");
      const stale = r.data?.stale_claimed_reset || 0;
      const requeued = r.data?.failed_requeued || 0;
      if (stale === 0 && requeued === 0) {
        toast.success("Δεν χρειάστηκε επιδιόρθωση");
      } else {
        toast.success(
          `Επιδιορθώθηκαν: ${stale} stuck + ${requeued} failed jobs`,
        );
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία επιδιόρθωσης");
    } finally {
      setRepairing(false);
    }
  };

  const key = loading ? "loading" : (health?.status || "ok");
  const meta = STATUS_MAP[key] || STATUS_MAP.loading;
  const Ico = meta.Icon;
  const isSpin = key === "loading" || repairing;

  // Compact badge; renders in top-bar next to other icons.
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ftext-health-badge"
        title="FTEXT / Ταμειακή — κατάσταση"
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold border transition ${meta.btn}`}
      >
        <Ico size={13} className={isSpin ? "animate-spin" : ""} />
        <span>{meta.label}</span>
        {health?.pending > 0 && (
          <span className="ml-1 px-1 rounded bg-white/80 text-[10px]">
            {health.pending}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ftext-health-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PrinterCheck className={meta.icon} size={22} />
              Κατάσταση FTEXT / Ταμειακή
            </DialogTitle>
            <DialogDescription>
              {key === "ok" && "Ο τοπικός agent επικοινωνεί κανονικά με την ταμειακή."}
              {key === "degraded" && "Παρατηρείται καθυστέρηση ή μερικά σφάλματα."}
              {key === "critical" && "Ο τοπικός agent είναι offline ή υπάρχουν κολλημένες αποδείξεις."}
              {key === "loading" && "Φόρτωση κατάστασης..."}
            </DialogDescription>
          </DialogHeader>

          {health && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <MetricRow label="Σε αναμονή" value={health.pending}
                           accent={health.pending > 5 ? "amber" : "zinc"} />
                <MetricRow label="Σε επεξεργασία" value={health.claimed}
                           accent={health.stuck_claimed?.length ? "rose" : "zinc"} />
                <MetricRow label="Επιτυχή (τελ. 1ω)" value={health.done_recent} accent="emerald" />
                <MetricRow label="Απέτυχαν (τελ. 1ω)" value={health.failed_recent}
                           accent={health.failed_recent > 0 ? "rose" : "zinc"} />
                <MetricRow label="Σφάλμα «108» (τελ. 1ω)" value={health.error_108_recent}
                           accent={health.error_108_recent > 3 ? "rose" : "zinc"} />
                <MetricRow label="Agent last poll" value={fmtAge(health.agent_last_poll_age)}
                           accent={health.agent_last_poll_age > 30 ? "rose" : "emerald"} />
              </div>

              {health.stuck_claimed?.length > 0 && (
                <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-xs">
                  <div className="font-bold text-rose-800 mb-1">
                    Κολλημένες αποδείξεις:
                  </div>
                  {health.stuck_claimed.slice(0, 5).map((j) => (
                    <div key={j.id} className="font-mono text-rose-700">
                      #{String(j.id).slice(0, 8)} · {j.kind || "?"} · {j.age_sec}s
                    </div>
                  ))}
                </div>
              )}

              {health.verdict_reasons?.length > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs">
                  <div className="font-bold text-amber-800 mb-1">Λόγοι:</div>
                  <ul className="list-disc list-inside text-amber-700">
                    {health.verdict_reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="ftext-health-refresh"
            >
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
              Ανανέωση
            </Button>
            <Button
              onClick={runRepair}
              disabled={repairing}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              data-testid="ftext-health-repair"
            >
              <Wrench size={14} className={`mr-1 ${repairing ? "animate-spin" : ""}`} />
              {repairing ? "Επιδιόρθωση…" : "Επιδιόρθωση Τώρα"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-alert (Feb 2026): ανοίγει μόνο του όταν το FTEXT γίνει
          critical. Ο χρήστης μπορεί να πατήσει «Επιδιόρθωση Τώρα» ή
          να το κάνει snooze για 5/30 λεπτά. */}
      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="max-w-md border-rose-300" data-testid="ftext-alert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <BellRing className="animate-pulse" size={22} />
              Πρόβλημα με την Ταμειακή!
            </DialogTitle>
            <DialogDescription className="text-slate-700 text-base pt-2">
              {health?.verdict_reasons?.length > 0 ? (
                <ul className="list-disc list-inside space-y-1">
                  {health.verdict_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              ) : (
                "Ο τοπικός agent δεν επικοινωνεί σωστά με την ταμειακή."
              )}
              <div className="mt-3 text-xs text-slate-500">
                Οι αποδείξεις σου δεν αποστέλλονται μέχρι να λυθεί το πρόβλημα.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => snoozeAlert(5)}
              data-testid="ftext-alert-snooze-5"
            >
              5&apos; αργότερα
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => snoozeAlert(30)}
              data-testid="ftext-alert-snooze-30"
            >
              30&apos; αργότερα
            </Button>
            <Button
              onClick={async () => {
                await runRepair();
                setAlertOpen(false);
              }}
              disabled={repairing}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              data-testid="ftext-alert-repair"
            >
              <Wrench size={14} className={`mr-1 ${repairing ? "animate-spin" : ""}`} />
              {repairing ? "Επιδιόρθωση…" : "Επιδιόρθωση Τώρα"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MetricRow({ label, value, accent = "zinc" }) {
  const cls = METRIC_ACCENTS[accent] || METRIC_ACCENTS.zinc;
  const lbl = METRIC_ACCENT_LABEL[accent] || METRIC_ACCENT_LABEL.zinc;
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className={`text-[10px] uppercase tracking-wide ${lbl}`}>{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}
